/**
 * Arc layer factory for route and connection visualization.
 * Used for trade-routes, shipping-lanes, arctic-shipping.
 */

import { ArcLayer } from '@deck.gl/layers';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs that render as generic arcs (via this module).
 *  displacement has a dedicated renderer in displacement-layer.ts.
 *  Note: trade-routes now renders via trade-route-layer.ts (ArcLayer + greatCircle). */
export const ARC_LAYER_IDS = new Set([
  'shipping-lanes', 'arctic-shipping',
]);

/** Event data with source/target coordinates for arc rendering. */
export interface ArcDatum {
  event: NormalizedEvent;
  source: [number, number];
  target: [number, number];
  width?: number;
}

/** Color palette by arc layer type — matching WorldMonitor. */
const ARC_COLORS: Record<string, { source: [number, number, number, number]; target: [number, number, number, number] }> = {
  'trade-routes': {
    source: [68, 204, 136, 13],
    target: [68, 204, 136, 153],
  },
  'shipping-lanes': {
    source: [68, 136, 255, 13],
    target: [68, 136, 255, 153],
  },
  'arctic-shipping': {
    source: [147, 197, 253, 25],
    target: [147, 197, 253, 160],
  },
  'displacement': {
    source: [255, 158, 11, 25],
    target: [255, 68, 68, 160],
  },
};

const DEFAULT_ARC_COLOR = {
  source: [100, 116, 139, 140] as [number, number, number, number],
  target: [100, 116, 139, 140] as [number, number, number, number],
};

/**
 * Extract arc data from events.
 * Events with `raw.source` and `raw.target` coordinates are treated as arc data.
 * Single-point events are skipped (arcs require two endpoints).
 */
export function extractArcData(events: NormalizedEvent[]): ArcDatum[] {
  const arcs: ArcDatum[] = [];

  for (const event of events) {
    const raw = event.raw as Record<string, unknown> | undefined;
    if (!raw) continue;

    const src = raw.sourceCoord as [number, number] | undefined;
    const tgt = raw.targetCoord as [number, number] | undefined;
    const width = typeof raw.width === 'number' ? raw.width : undefined;

    if (src && tgt && Array.isArray(src) && Array.isArray(tgt)) {
      arcs.push({ event, source: src, target: tgt, width });
    }
  }

  return arcs;
}

/** Create an ArcLayer for the given layer type. */
export function createArcLayer(
  layerId: string,
  events: NormalizedEvent[],
  zoom: number,
): ArcLayer<ArcDatum> {
  const arcData = extractArcData(events);
  const colors = ARC_COLORS[layerId] ?? DEFAULT_ARC_COLOR;
  const opacity = Math.min(0.8, 0.2 + zoom * 0.08);

  return new ArcLayer<ArcDatum>({
    id: `arc-${layerId}`,
    data: arcData,
    pickable: true,
    getSourcePosition: (d) => d.source,
    getTargetPosition: (d) => d.target,
    getSourceColor: colors.source,
    getTargetColor: colors.target,
    getWidth: (d) => d.width ?? 1.5,
    opacity,
    greatCircle: true,
  });
}
